import React from 'react';
import { Check, Package, Save, Users } from 'lucide-react';

import { getProductTypeName } from '@/lib/constants';
import {
  Card, PhonePage, PrimaryButton, SearchBar, StatusPill, TopBar,
} from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';
import PhoneSpecFields from './PhoneSpecFields';

/**
 * Adding a product to the catalog, on a phone.
 *
 * The desktop asks the same three questions in a three-column card — name,
 * type, minimum — over a two-column spec grid; at a handset's width the
 * columns collapse into one anyway, but with 36px controls and native
 * dropdowns. Here the choice of path is two tiles, the customer and their past
 * order lines are tappable rows, and the details are 48px controls under their
 * labels. The state lives on the page; this only draws it.
 */

function Row({ selected, onClick, testID, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testID}
      className="flex min-h-[48px] w-full items-center justify-between gap-3 border-b px-1 py-2.5 text-left last:border-b-0"
      style={{ borderColor: Colors.border.subtle }}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {selected && <Check className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.accent.DEFAULT }} />}
    </button>
  );
}

function ModeTile({ icon: Icon, title, hint, onClick, testID }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testID}
      className="mt-3 w-full rounded-[14px] border p-4 text-left"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: Colors.accent.wash }}>
        <Icon className="h-5 w-5" style={{ color: Colors.accent.DEFAULT }} />
      </span>
      <span className="mt-3 block text-[15px] font-bold" style={{ color: Colors.text.primary }}>{title}</span>
      <span className="mt-1 block text-[12px] leading-relaxed" style={{ color: Colors.text.secondary }}>{hint}</span>
    </button>
  );
}

export default function PhoneCreateCatalogItem({
  mode, onPickMode, onBack,
  itemName, setItemName, productTypes, selectedProductType, onProductType,
  minStockThreshold, setMinStockThreshold,
  customers, onCustomerSearch, selectedCustomerId, onCustomerSelect,
  customerOrders, loadingOrders, selectedOrderItem, onSelectOrderItem,
  jobCardFields, jobCardFieldValues, onFieldChange, onPlyChange,
  loading, onSubmit,
}) {
  if (mode === 'choose') {
    return (
      <PhonePage testID="create-catalog-item-page">
        <TopBar eyebrow="CATALOG" title="Add catalog item" onBack={onBack} />
        <div className="flex-1 px-4 pb-10">
          <p className="mt-4 text-[13px]" style={{ color: Colors.text.secondary }}>
            Choose how you want to add a new catalog item.
          </p>
          <ModeTile
            icon={Users} title="For a specific customer" testID="mode-customer"
            hint="Pick from their past orders and the specs come along. For saving repeat products."
            onClick={() => onPickMode('customer')}
          />
          <ModeTile
            icon={Package} title="General" testID="mode-general"
            hint="Not tied to any customer. Pick the product type and fill in the specs by hand."
            onClick={() => onPickMode('general')}
          />
        </div>
      </PhonePage>
    );
  }

  const typeOptions = productTypes.map((pt) => ({ value: pt.code, label: pt.name }));
  const customerStep = mode === 'customer';

  return (
    <PhonePage testID="create-catalog-item-page">
      <TopBar
        eyebrow="ADD CATALOG ITEM"
        title={customerStep ? 'Customer specific' : 'General'}
        subtitle={customerStep ? 'Pick from what they ordered before' : 'Pick a type and fill in the specs'}
        onBack={() => onPickMode('choose')}
      />

      <div className="flex-1 px-4 pb-10">
        {customerStep && (
          <Card className="mt-3" title="Customer">
            <SearchBar placeholder="Search customers…" onChange={onCustomerSearch} testID="customer-search-input" />
            <div className="mt-2 max-h-[16rem] overflow-y-auto">
              {customers.map((c) => (
                <Row key={c.id} selected={selectedCustomerId === c.id} onClick={() => onCustomerSelect(c.id)} testID={`customer-${c.id}`}>
                  <span className="block truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>{c.customer_name}</span>
                  {!!c.firm_name && <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>{c.firm_name}</span>}
                </Row>
              ))}
            </div>
          </Card>
        )}

        {customerStep && selectedCustomerId && (
          <Card className="mt-3" title="Past order item" aside="optional">
            {loadingOrders ? (
              <p className="py-3 text-[13px]" style={{ color: Colors.text.tertiary }}>Loading order history…</p>
            ) : customerOrders.length === 0 ? (
              <p className="py-3 text-[13px]" style={{ color: Colors.text.tertiary }}>
                No past order items found. Fill in the details below.
              </p>
            ) : (
              <div className="max-h-[16rem] overflow-y-auto">
                {customerOrders.map((item) => (
                  <Row
                    key={`${item.order_id}-${item.line_item_id}`}
                    selected={selectedOrderItem?.line_item_id === item.line_item_id}
                    onClick={() => onSelectOrderItem(item)}
                    testID={`order-item-${item.line_item_id}`}
                  >
                    <span className="block truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>{item.item_name}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-2 text-[11px]" style={{ color: Colors.text.tertiary }}>
                      {item.order_display_id}
                      <StatusPill label={getProductTypeName(item.product_type, true)} />
                      Qty {item.quantity}
                    </span>
                  </Row>
                ))}
              </div>
            )}
          </Card>
        )}

        <Card className="mt-3" title="Item details">
          <Field label="Product name *">
            <input
              value={itemName} onChange={(e) => setItemName(e.target.value)}
              placeholder="e.g., Burger Box 6-inch" data-testid="item-name"
              className={INPUT_CLASS} style={inputStyle}
            />
          </Field>
          <Field label="Product type *" className="mt-3">
            <SelectField name="product_type" label="Product type" value={selectedProductType} options={typeOptions} onChange={onProductType} placeholder="Select type" />
          </Field>
          <Field label="Minimum stock" className="mt-3">
            <input
              type="number" inputMode="numeric" value={minStockThreshold}
              onChange={(e) => setMinStockThreshold(e.target.value)}
              placeholder="0" data-testid="min-stock"
              className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
            />
          </Field>
        </Card>

        {selectedProductType && jobCardFields.length > 0 && (
          <PhoneSpecFields
            fields={jobCardFields}
            values={jobCardFieldValues}
            onChange={onFieldChange}
            onPlyChange={onPlyChange}
            orderType={selectedProductType}
          />
        )}

        <PrimaryButton className="mt-5" onClick={onSubmit} busy={loading} disabled={!itemName || !selectedProductType} testID="create-item-btn">
          <Save className="h-4 w-4" /> Create catalog item
        </PrimaryButton>
      </div>
    </PhonePage>
  );
}
